import * as Sentry from '@sentry/vue'
import { useEventListener, useIdle, useLocalStorage } from '@vueuse/core'
import { i18n } from 'boot/i18n'
import DialogConfirm from 'components/dialogs/DialogConfirm.vue'
import DialogModal from 'components/dialogs/DialogModal.vue'
import { useAuth } from 'feathers-pinia'
import { DateTime } from 'luxon'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { Dialog, Loading, Notify, QSpinner } from 'quasar'
import { isJwtExpired, isJwtValid } from 'src/utils/jwt.js'
import { getStorageKey, isCordovaAndroid, sleep } from 'src/utils/utils.js'

export const useAuthStore = defineStore('auth', () => {
  const userStore = useUserStore()
  const UIStore = useUIStore()
  const { user } = storeToRefs(userStore)

  let paused = ref(false)
  let countdownTimer = null
  let updateNotify = null
  const { idle: idleWarning, reset: resetIdleWarning } = useIdle((process.env.IDLE_TIMEOUT - process.env.IDLE_TIMEOUT_WARNING_COUNTER) * 1000)
  const { idle, reset: resetIdle } = useIdle(parseInt(process.env.IDLE_TIMEOUT) * 1000)

  const totpIdentifier = useLocalStorage('totpIdentifier', '')
  const authenticationTimer = useLocalStorage('authenticationTimer', null)
  const isBiometricsEnabled = useLocalStorage('isBiometricsEnabled', false)
  const rememberMFA = useLocalStorage('rememberMFA', false)
  const keepMeLoggedIn = useLocalStorage('keepMeLoggedIn', false)
  const loginRedirect = useLocalStorage('loginRedirect', '')
  const accessToken = useLocalStorage(getStorageKey('access'), '')
  const refreshToken = useLocalStorage(getStorageKey('refresh'), '')

  const appUpdateSuccess = async (str) => {
    if (str === 'RESULT_CANCELED') {
      cordova.plugins.InAppUpdate.updateImmediate(appUpdateSuccess, appUpdateFailed)
    }
  }

  const appUpdateFailed = (str) => {
    console.error('FAILED: ' + str)
  }

  const updateTimer = () => {
    authenticationTimer.value = DateTime.now().toSeconds().toString()
  }

  // These events are the same ones that are used for useIdle
  const events = ['mousemove', 'mousedown', 'resize', 'keydown', 'touchstart', 'wheel']

  events.forEach((evt) => {
    useEventListener(window, evt, updateTimer, { passive: true })
  })

  const api = useFeathers()

  const authResponse = ref(null)
  const archivedClientPermissionContext = ref({})
  const ecdSettings = ref({
    isLoaded: false,
    isFailed: false,
  })

  const updateRetryHash = (enabled) => {
    if (typeof window === 'undefined') {
      return
    }

    const url = new URL(window.location.href)

    if (enabled) {
      url.hash = 'retry'
    } else if (url.hash === '#retry') {
      url.hash = ''
    }

    window.history.replaceState({}, '', url.toString())
  }

  const hasRetryHash = () => {
    if (typeof window === 'undefined') {
      return false
    }

    return window.location.hash === '#retry'
  }

  const auth = useAuth({
    api,
    servicePath: 'user',
    onSuccess: async (result) => {
      await setAuthResponse(result, false)
    },
    onInitSuccess: async (result) => {
      await setAuthResponse(result, false)
    },
  })

  watch(
    () => authResponse.value,
    (newValue) => {
      user.value = newValue?.user
    },
  )

  // Functions like verifyMFA may set authResponse outside the usual auth flow
  const setAuthResponse = async (response, reAuthenticate = true) => {
    authResponse.value = response
    if (response?.accessToken) {
      await api.authentication.setAccessToken(response.accessToken)
      api.authentication.authenticated = true
      api.authentication.app.set('authentication', Promise.resolve(response))
      accessToken.value = response.accessToken
    }
    if (response?.refreshToken) {
      refreshToken.value = response.refreshToken
    }
    if (response.authentication?.payload?.identifier) {
      totpIdentifier.value = response.authentication.payload.identifier
    }

    const wasAlreadyLoggedIn = accessToken.value !== null && accessToken.value !== ''

    if (reAuthenticate) {
      await api.reAuthenticate(true)
    }

    if (!wasAlreadyLoggedIn && response.authentication?.payload?.mfa === true) {
      initBiometrics()
    }
  }

  const hasFeature = (feature) => {
    return computed(() => ecdSettings.value[feature] ?? false)
  }

  function isStaff() {
    return computed(() => authResponse.value?.user?.is_staff === true)
  }

  function isSuperUser() {
    return computed(() => (authResponse.value?.user?.is_superuser === true && authResponse.value?.user?.is_staff) ?? false)
  }

  function hasClientPermission(client, permission, checkOwnerPermission = false, ownerId = null) {
    if (!client) {
      return computed(() => false)
    }

    let clientId = client
    if (typeof clientId === 'object') {
      clientId = client.id
    }
    clientId = String(clientId)

    // Split permission to add 'mine' before the model part of the permission
    const splitPermission = permission.split('_')
    splitPermission.splice(1, 0, 'mine')
    const minePermission = splitPermission.join('_')

    return computed(() => {
      const archivedPermissions = archivedClientPermissionContext.value?.[clientId]
      const isArchivedClient = (typeof client === 'object' && client.deleted) || !!archivedPermissions
      const mutationPrefixes = [
        'add_',
        'change_',
        'delete_',
        'copy_',
        'cancel_',
        'restore_',
        'revert_',
        'start_sign_',
        'sign_',
        'expire_',
        'finalize_',
        'update_',
        'move_',
        'generate_',
        'process_',
        'bulk_assign_',
      ]

      if (isArchivedClient && mutationPrefixes.some((prefix) => permission.startsWith(prefix))) {
        return false
      }

      if (isSuperUser().value) {
        return true
      }

      if (checkOwnerPermission && !ownerId) {
        return false
      }

      // Default to true, so that permission is not denied when checkOwnerPermission is false
      const ownerPermission = !checkOwnerPermission || ownerId === authResponse.value?.user?.id

      if (archivedPermissions) {
        return ((checkOwnerPermission && ownerPermission && archivedPermissions.includes(minePermission)) || archivedPermissions.includes(permission)) ?? false
      }

      return (
        ((checkOwnerPermission && ownerPermission && authResponse.value?.user?.user_permissions?.client?.[clientId]?.includes(minePermission)) ||
          authResponse.value?.user?.user_permissions?.client?.[clientId]?.includes(permission)) ??
        false
      )
    })
  }

  function setArchivedClientPermissionContext(context) {
    if (!context?.client || !Array.isArray(context.permissions)) {
      return
    }

    archivedClientPermissionContext.value = {
      ...archivedClientPermissionContext.value,
      [String(context.client)]: context.permissions,
    }
  }

  function clearArchivedClientPermissionContext(client = null) {
    if (!client) {
      archivedClientPermissionContext.value = {}
      return
    }

    const clientId = String(typeof client === 'object' ? client.id : client)
    const nextContext = { ...archivedClientPermissionContext.value }
    delete nextContext[clientId]
    archivedClientPermissionContext.value = nextContext
  }

  function hasClientPermissionSomewhere(permission, checkOwnerPermission = false, ownerId = null) {
    if (isSuperUser().value) {
      return computed(() => true)
    }

    return computed(() => {
      if (user.value?.user_permissions?.client) {
        for (const client of Object.keys(user.value.user_permissions.client)) {
          if (hasClientPermission(client, permission, checkOwnerPermission, ownerId).value) {
            return true
          }
        }
      }

      return false
    })
  }

  function hasLocationPermission(location, permission, checkOwnerPermission = false, ownerId = null) {
    if (isSuperUser().value) {
      return computed(() => true)
    }

    let locationId = location
    if (typeof locationId === 'object') {
      locationId = location.id
    }

    // Default to true, so that permission is not denied when checkOwnerPermission is false
    let ownerPermission = true

    // Split permission to add 'mine' before the model part of the permission
    const splitPermission = permission.split('_')
    splitPermission.splice(1, 0, 'mine')
    const minePermission = splitPermission.join('_')

    if (checkOwnerPermission) {
      if (!ownerId) {
        return computed(() => false)
      }

      ownerPermission = ownerId === authResponse.value?.user?.id
    }

    return computed(
      () =>
        ((checkOwnerPermission && ownerPermission && authResponse.value?.user?.user_permissions?.location?.[locationId]?.includes(minePermission)) ||
          authResponse.value?.user?.user_permissions?.location?.[locationId]?.includes(permission)) ??
        false,
    )
  }

  function hasLocationPermissionSomewhere(permission, checkOwnerPermission = false, ownerId = null) {
    if (isSuperUser().value) {
      return computed(() => true)
    }

    return computed(() => {
      if (user.value?.user_permissions?.location) {
        for (const location of Object.keys(user.value.user_permissions.location)) {
          if (hasLocationPermission(location, permission, checkOwnerPermission, ownerId).value) {
            return true
          }
        }
      }

      return false
    })
  }

  function hasUserPermission(permission, checkOwnerPermission = false, ownerId = null) {
    if (isSuperUser().value) {
      return computed(() => true)
    }

    // Default to true, so that permission is not denied when checkOwnerPermission is false
    let ownerPermission = true

    // Split permission to add 'mine' before the model part of the permission
    const splitPermission = permission.split('_')
    splitPermission.splice(1, 0, 'mine')
    const minePermission = splitPermission.join('_')

    if (checkOwnerPermission) {
      if (!ownerId) {
        return computed(() => false)
      }

      ownerPermission = ownerId === authResponse.value?.user?.id
    }

    return computed(() => {
      return (
        ((checkOwnerPermission && ownerPermission && authResponse.value?.user?.user_permissions?.user?.includes(minePermission)) ||
          authResponse.value?.user?.user_permissions?.user?.includes(permission)) ??
        false
      )
    })
  }

  function hasFileTreePermission(authObj, action = 'add', type, name, authSrc, checkOwnerPermission = false, ownerId = null) {
    if ((!authObj && ['client', 'location'].includes(authSrc)) || !type || !name) {
      return computed(() => false)
    }

    const permission = `${action}_filetree${type}_${name}_${['client', 'location'].includes(authSrc) ? authSrc : 'filetree'}`

    if (authSrc === 'client') {
      return hasClientPermission(authObj, permission, checkOwnerPermission, ownerId)
    }

    if (isSuperUser().value) {
      return computed(() => true)
    }

    if (authSrc === 'location') {
      return hasLocationPermission(authObj, permission, checkOwnerPermission, ownerId)
    }

    return hasUserPermission(permission, checkOwnerPermission, ownerId)
  }

  function hasMessageBoardPermission(authObj, action = 'add', name, authSrc, checkOwnerPermission = false, ownerId = null) {
    if ((!authObj && !!authSrc) || !name) {
      return computed(() => false)
    }

    const permission = `${action}_messageboardmessage_${name}_${authSrc ?? 'messageboard'}`

    if (authSrc === 'client') {
      return hasClientPermission(authObj, permission, checkOwnerPermission, ownerId)
    }

    if (isSuperUser().value) {
      return computed(() => true)
    }

    if (authSrc === 'location') {
      return hasLocationPermission(authObj, permission, checkOwnerPermission, ownerId)
    }

    return hasUserPermission(permission, checkOwnerPermission, ownerId)
  }

  async function loadEcdSettings(showSpinnerImmediately = false) {
    if (isCordovaAndroid() && !showSpinnerImmediately) {
      await sleep(1500)
    } else {
      Loading.show({
        spinner: QSpinner,
        delay: showSpinnerImmediately ? 0 : 400,
        spinnerColor: 'primary',
        backgroundColor: 'white',
      })
    }

    ecdSettings.value.isLoaded = false
    const isRetryAttempt = showSpinnerImmediately
    const isHardRefreshFollowUp = !isRetryAttempt && hasRetryHash()
    let shouldHardReload = false

    if (isRetryAttempt) {
      updateRetryHash(true)
    }

    try {
      const data = await api
        .service('ecd-settings/')
        .find({})
        .catch((e) => {
          if (isRetryAttempt) {
            Sentry.captureException(e, {
              extra: {
                message: 'Failed to load ECD settings, user pressed try again button',
              },
            })
            shouldHardReload = true
          } else {
            Sentry.captureException(e)

            if (isHardRefreshFollowUp) {
              Sentry.captureMessage('Zelfs met een hard refresh werkt het ECD nog niet.')
              updateRetryHash(false)
            }
          }
          return {
            isFailed: true,
          }
        })
      data.isLoaded = true
      ecdSettings.value = data

      if ((isRetryAttempt || isHardRefreshFollowUp) && !data.isFailed) {
        Sentry.captureMessage('Na de reload is het ECD weer werkend geworden.')
        updateRetryHash(false)
      }
    } finally {
      Loading.hide()

      if (shouldHardReload) {
        window.location.reload()
      }
    }
  }

  function initLogoffTimer() {
    if (isCordovaAndroid()) {
      // Attach event listeren for "deviceready" which fires only once when the app starts
      document.addEventListener('deviceready', async () => {
        cordova.plugins.InAppUpdate.updateImmediate(appUpdateSuccess, appUpdateFailed)

        // Attach event listener for "pause" which is triggered when the app is minimized
        document.addEventListener('pause', async () => {
          paused.value = true
          updateTimer()
          resetIdle()
          resetIdleWarning()
        })

        // Attach event listener for "resume" which is triggered when the app is resumed after minimizing it
        document.addEventListener('resume', async () => {
          resetIdle()
          resetIdleWarning()
          paused.value = false

          // Do not do anything with fingerprints when on login page
          if (window.location.pathname.startsWith('/auth')) {
            return
          }

          if (!accessToken.value) {
            return
          }

          await nextTick()

          if (isJwtExpired(refreshToken.value, DateTime.now().toSeconds())) {
            fullLogout(true)
            return
          }

          if (DateTime.now().toSeconds() - dialogRefIgnore.value < 10) {
            // Ignore resume event if a dialog was already shown by the idle tracker
          } else {
            if (isBiometricsEnabled.value) {
              if (
                authenticationTimer.value &&
                DateTime.now().toSeconds() - parseInt(authenticationTimer.value) > parseInt(process.env.LEAVE_APP_TIMEOUT_FINGERPRINT)
              ) {
                await auth.reAuthenticate()
                promptForBiometrics(false)
              }
            } else {
              if (authenticationTimer.value && DateTime.now().toSeconds() - parseInt(authenticationTimer.value) > parseInt(process.env.LEAVE_APP_TIMEOUT)) {
                fullLogout(true)
              } else {
                cordova.plugins.InAppUpdate.updateImmediate(appUpdateSuccess, appUpdateFailed)
              }
            }
          }
        })

        if (!accessToken.value) {
          return
        }

        if (isJwtExpired(refreshToken.value, DateTime.now().toSeconds())) {
          fullLogout(true)
          return
        }

        // If the user starts the app and previously was logged in, but more than 15 minutes ago, log the user out completely
        if (authenticationTimer.value && DateTime.now().toSeconds() - parseInt(authenticationTimer.value) > parseInt(process.env.LEAVE_APP_TIMEOUT)) {
          if (isBiometricsEnabled.value) {
            await auth.reAuthenticate()
            promptForBiometrics(false)
          } else {
            fullLogout(true)
          }
        } else {
          // Do not do anything with fingerprints when on login page
          if (!window.location.pathname.startsWith('/auth')) {
            // Otherwise, when the app starts, prompt to confirm biometrics if enabled
            if (isBiometricsEnabled.value) {
              await auth.reAuthenticate()
              promptForBiometrics(false)
            }
            // Or ask the user to setup biometrics if not yet enabled
            else {
              await auth.reAuthenticate()
              initBiometrics()
            }
          }
        }
      })
    } else {
      if (authenticationTimer.value && DateTime.now().toSeconds() - parseInt(authenticationTimer.value) > parseInt(process.env.LEAVE_APP_TIMEOUT)) {
        fullLogout(true)
      } else {
        auth.reAuthenticate()
      }
    }
  }

  function initBiometrics() {
    window.Fingerprint?.isAvailable(
      (result) => {
        if (!isBiometricsEnabled.value) {
          promptToEnableBiometrics()
        } else {
          promptForBiometrics(false)
        }
      },
      (error) => {
        if (isBiometricsEnabled.value) {
          fullLogout(true, 'failed_biometrics')
        }
      },
      {
        'allowBackup ': true,
      },
    )
  }

  function promptForBiometrics(initialSetup) {
    if (dialogRef.value !== null) {
      return
    }

    if (!initialSetup) {
      dialogRef.value?.hide()
      dialogRef.value = Dialog.create({
        persistent: true,
        component: DialogModal,
        componentProps: {
          title: i18n.global.t('Globals.Biometrics.ECD app locked.Title'),
          message: i18n.global.t('Globals.Biometrics.ECD app locked.Message'),
        },
        parent: this,
      })
    }

    window.Fingerprint?.show(
      {
        title: i18n.global.t('Globals.Biometrics.Unlock ECD app.Title'),
        //disableBackup: true,
        cancelButtonTitle: i18n.global.t('Globals.Biometrics.Unlock ECD app.cancelButtonTitle'),
        description: i18n.global.t('Globals.Biometrics.Unlock ECD app.description'),
      },
      () => {
        dialogRefIgnore.value = DateTime.now().toSeconds()
        isBiometricsEnabled.value = true
        dialogRef.value?.hide()
        dialogRef.value = null
        if (initialSetup) {
          Notify.create({
            type: 'positive',
            progress: true,
            message: i18n.global.t('Globals.Biometrics.Biometrics activated successfully'),
          })
        }

        resetIdleWarning()
        resetIdle()
        cordova.plugins.InAppUpdate.updateImmediate(appUpdateSuccess, appUpdateFailed)
      },
      async (error) => {
        if (initialSetup) {
          Notify.create({
            type: 'negative',
            progress: true,
            message: i18n.global.t('Globals.Biometrics.Biometrics activation failed'),
          })
          dialogRef.value?.hide()
          dialogRef.value = null

          resetIdleWarning()
          resetIdle()
        } else {
          if (error.code === -111 || error.code === -112) {
            fullLogout(true, 'failed_biometrics_blocked')
          } else {
            fullLogout(true, 'failed_biometrics')
          }
        }
      },
    )
  }

  const dialogRef = ref(null)
  const dialogRefIgnore = ref(null)

  function promptToEnableBiometrics() {
    if (!accessToken.value) {
      return
    }

    dialogRef.value?.hide()
    dialogRef.value = Dialog.create({
      persistent: true,
      component: DialogConfirm,
      componentProps: {
        title: i18n.global.t('Globals.Biometrics.Setup biometrics.Title'),
        message: i18n.global.t('Globals.Biometrics.Setup biometrics.Message'),
      },
      parent: this,
    })
      .onOk(async (payload) => {
        dialogRef.value = null
        promptForBiometrics(true)
      })
      .onDismiss(() => {
        dialogRef.value = null
      })
  }

  async function fullLogout(redirect = true, error = undefined, locationReload = false) {
    updateTimer()

    keepMeLoggedIn.value = false
    loginRedirect.value = ''
    if (isJwtValid(accessToken.value, DateTime.now().toSeconds())) {
      try {
        await auth.logout()
      } catch {
        // Server-side logout is best effort; local logout must always continue.
      }
    }

    authenticationTimer.value = null
    //alert('CLEAR AT TOKEN FULLLOGOUT')
    accessToken.value = null
    refreshToken.value = null
    await api.authentication.reset()
    dialogRef.value?.hide()
    dialogRef.value = null

    // Reset Client tabs and last active client route
    UIStore.resetClientTabs()
    clearArchivedClientPermissionContext()

    resetIdleWarning()
    resetIdle()
    if (countdownTimer) {
      clearInterval(countdownTimer)
    }
    if (updateNotify) {
      updateNotify()
    }
    if (!rememberMFA.value) {
      totpIdentifier.value = ''
    }

    if (redirect) {
      if (!locationReload) {
        const { Router } = await import('src/router/index.js')
        await Router.replace({ name: 'login', query: { error } })
      } else {
        window.location.href = '/'
      }
    }
  }

  async function refreshAccessToken() {
    try {
      const response = await api.service('authentication').create({
        strategy: 'refresh',
        refreshToken: refreshToken.value,
      })

      if (!response?.accessToken) {
        return false
      }

      await setAuthResponse(response, false)
      return accessToken.value
    } catch {
      return false
    }
  }

  function clearLoginRedirect() {
    loginRedirect.value = ''
  }

  watch(
    () => idleWarning.value,
    (isIdle) => {
      if (isIdle && accessToken.value && !window.location.pathname.startsWith('/auth')) {
        const logoutAt = DateTime.now().toSeconds() + parseInt(process.env.IDLE_TIMEOUT_WARNING_COUNTER)

        // create one notification and capture its “update” callback
        updateNotify = Notify.create({
          type: 'warning',
          timeout: 0,
          group: false,
          message: i18n.global.t('Globals.Notifications.Automatic logout', { secLeft: Math.round(logoutAt - DateTime.now().toSeconds()) }, 2),
        })
        // every second, recalc remaining time and call updateNotify
        countdownTimer = setInterval(() => {
          if (!accessToken.value || window.location.pathname.startsWith('/auth')) {
            clearInterval(countdownTimer)
            updateNotify()
            return
          }

          const secLeft = Math.round(logoutAt - DateTime.now().toSeconds())
          if (secLeft <= 0) {
            // time’s up: stop interval, update one last time (or just close), then log out
            if (countdownTimer) {
              clearInterval(countdownTimer)
              countdownTimer = null
              if (updateNotify) {
                updateNotify()
              }
            }
          } else {
            const message = i18n.global.t('Globals.Notifications.Automatic logout', { secLeft }, secLeft === 1 ? 1 : 2)
            updateNotify({
              message,
            })
          }
        }, 1000)
      } else {
        if (countdownTimer) {
          clearInterval(countdownTimer)
          countdownTimer = null
          if (updateNotify) {
            updateNotify()
          }
        }
      }
    },
  )

  watch(
    () => idle.value,
    (newValue) => {
      if (newValue === true && !paused.value && accessToken.value && !window.location.pathname.startsWith('/auth')) {
        if (isBiometricsEnabled.value) {
          if (!dialogRef.value) {
            promptForBiometrics(false)
          }
        } else {
          fullLogout(true, 'timeout')
        }
      }
    },
  )

  initLogoffTimer()

  return {
    ecdSettings,
    hasFeature,
    isStaff,
    isSuperUser,
    loadEcdSettings,
    totpIdentifier,
    rememberMFA,
    keepMeLoggedIn,
    fullLogout,
    setAuthResponse,
    authResponse,
    ...auth,
    accessToken,
    refreshToken,
    loginRedirect,
    isBiometricsEnabled,
    clearLoginRedirect,
    hasClientPermission,
    hasClientPermissionSomewhere,
    setArchivedClientPermissionContext,
    clearArchivedClientPermissionContext,
    hasLocationPermission,
    hasLocationPermissionSomewhere,
    hasUserPermission,
    hasFileTreePermission,
    hasMessageBoardPermission,
    refreshAccessToken,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
